import React, { useCallback, useMemo, useState } from 'react'
import PopoverButton from 'components/shared/PopoverButton'
import { Menu, MenuItem } from '@material-ui/core'
import { AtlasIcon } from 'web-components/atlas-icon'
import { translate } from 'components/shared/internationalization'
import { AtlasButton } from 'components/atlas-material/button'
import { css } from '@emotion/css'
import { ColorBaseSkyBlue } from 'assets/styles/variables'
import EditPencilIcon from 'assets/icons/EditPencilIcon'
import DefaultStarIcon from 'assets/icons/DefaultStarIcon'
import DownLoadIconBlack from 'assets/icons/DowloadBlackIcon'
import TrashDeleteIconBlack from 'assets/icons/TrashDeleteIcon'
import ConfirmationDialog from 'components/shared/ConfirmationDialog'
import i18n from 'common/util/internationalization'
import TemplateDrawer from 'pages/minutes-templates/TemplateDrawer'
import {
    deleteTemplate,
    updateSitePreferredTemplate
} from 'businesslayer/networkrequests/templateService'
import { TEMPLATE_OVERLAY_OPTIONS } from 'common/constants/constants'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'

type Props = {
    onClickOptions: (optionValue: OptionType) => void
    templateList?: any
    reloadTemplateAfterDelete: (string) => void
    resetPaginationAndReload: () => void
    resetPaginationAndReloadWithCurrentSort: () => void
    setDefaultTemplateId: (string) => void
    downloadTemplateFile?: (id: string, templateTitle: string) => void
    defaultTemplateId: any
    setIsOpen: any
    isOpen: boolean
    setShowToast?: React.Dispatch<React.SetStateAction<boolean>>
    setToastType?: React.Dispatch<React.SetStateAction<string>>
    setToastMessage?: React.Dispatch<React.SetStateAction<string>>
}

const TemplatesCardOverflow = (props: Props) => {
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [showEdit, setShowEdit] = useState(false)

    const {
        templateList,
        reloadTemplateAfterDelete,
        setDefaultTemplateId,
        resetPaginationAndReloadWithCurrentSort,
        setShowToast,
        setToastType,
        setToastMessage
    } = props

    const isDefaultTemplate = templateList?.isDefault

    const handleCardOverflowClick = async (optionId, popoverOnClose) => {
        if (optionId === TEMPLATE_OVERLAY_OPTIONS.OPTION_DELETE) {
            setShowConfirmation(true)
        }
        if (optionId === TEMPLATE_OVERLAY_OPTIONS.OPTION_EDIT) {
            props.setIsOpen(true)
            setShowEdit(true)
        }
        if (optionId === TEMPLATE_OVERLAY_OPTIONS.OPTION_DEFAULT) {
            try {
                // Create object for metadata update
                const templateData = {
                    title: props?.templateList?.title || '',
                    description: props?.templateList?.description || '',
                    isDefault: true
                }
                const templateId = props?.templateList?.id
                popoverOnClose(optionId)
                // Update metadata using object (not FormData)
                await updateSitePreferredTemplate(props?.templateList?.id, templateData)
                resetPaginationAndReloadWithCurrentSort()
                setDefaultTemplateId(templateId)
            } catch (error) {
                console.error('Error setting default template:', error)
            }
        }
        if (optionId === TEMPLATE_OVERLAY_OPTIONS.OPTION_DOWNLOAD) {
            popoverOnClose(optionId)
            props.downloadTemplateFile?.(
                props?.templateList?.id,
                props?.templateList?.title
            )
        }
        popoverOnClose(optionId)
        const clickedOption = OPTIONS.find((o) => +o.id === +optionId)
        !!clickedOption && props.onClickOptions(clickedOption.value)
    }

    const handleDeleteAction = useCallback(async () => {
        setShowConfirmation(false)

        if (templateList?.id) {
            try {
                const res = await deleteTemplate(templateList.id)
                if (res.message === 'DELETION_SUCCESS') {
                    setShowToast?.(true)
                    setToastType?.('success')
                    setToastMessage?.(i18n.t('TEMPLATE_DELETE_SUCCESS'))
                } else {
                    setShowToast?.(true)
                    setToastType?.('error')
                    setToastMessage?.(i18n.t('TEMPLATE_DELETE_FAIL'))
                }
                // Update template list immediately
                reloadTemplateAfterDelete(templateList.id)
            } catch (error) {
                setShowToast?.(true)
                setToastType?.('error')
                setToastMessage?.(i18n.t('TEMPLATE_DELETE_FAIL'))
                console.error('Error deleting template:', error)
                // Update template list immediately even on error
                reloadTemplateAfterDelete(templateList.id)
            }
        }
    }, [templateList, reloadTemplateAfterDelete, setShowToast, setToastType, setToastMessage])

    const renderConfirmedAction = useMemo(
        () => (
            <ConfirmationDialog
                message={
                    isDefaultTemplate
                        ? i18n.t('MINUTES_TEMPLATES_DELETE_DEFAULT_TEMPLATE_MESSAGE')
                        : i18n.t('MINUTES_TEMPLATES_DELETE_TEMPLATE_MESSAGE')
                }
                title={
                    isDefaultTemplate
                        ? i18n.t('MINUTES_TEMPLATES_DELETE_PRFERED_TEMPLATE_TITLE')
                        : i18n.t('MINUTES_TEMPLATES_DELETE_TEMPLATE_TITLE')
                }
                onConfirm={handleDeleteAction}
                onCancel={() => setShowConfirmation(false)}
                confirmLabel={(translate('DELETE') as unknown) as string}
            />
        ),
        [handleDeleteAction, setShowConfirmation, isDefaultTemplate]
    )

    const reloadTemplateList = useCallback(
        (templateId) => {
            reloadTemplateAfterDelete(templateId)
        },
        [reloadTemplateAfterDelete]
    )

    // Define options inside component for dynamic i18n
    const OPTIONS: any[] = [
        {
            id: 1,
            value: 'DEFAULT',
            testid: 'MinutesCardOverflow_Default',
            dataAnalytics: 'MM-Minute-Template-Default',
            label: 'MINUTES_TEMPLATES_DEFAULT_TEMPLATE',
            icon: <DefaultStarIcon />
        },
        {
            id: 2,
            value: 'EDIT',
            testid: 'MinutesCardOverflow_Template_Edit',
            dataAnalytics: 'MM-Minute-Template-Edit',
            label: 'TEMPLATE_OPTION_EDIT_LABEL',
            icon: <EditPencilIcon />
        },
        {
            id: 3,
            value: 'DOWNLOAD',
            testid: 'MinutesCardOverflow_Template_Download',
            dataAnalytics: 'MM-Minute-Template-Download',
            label: 'MINUTES_TEMPLATES_DOWNLOAD_TEMPLATE',
            icon: <DownLoadIconBlack />
        },
        {
            id: 4,
            value: 'DELETE',
            testid: 'MinutesCardOverflow_Delete',
            dataAnalytics: 'MM-Template-Delete',
            label: 'DELETE',
            icon: <TrashDeleteIconBlack />
        }
    ]

    const confirmationDialog = showConfirmation ? renderConfirmedAction : null
    const editDialog = showEdit ? (
        <TemplateDrawer
            templateList={props.templateList}
            isUpdateTemplateClicked={showEdit}
            setIsOpen={setShowEdit}
            reloadTemplateList={reloadTemplateList}
            downloadTemplateFile={props.downloadTemplateFile}
            resetPaginationAndReload={props.resetPaginationAndReload}
            isOpen={showEdit}
            uploadStatus={null}
            fileUploadError={null}
            setShowToast={setShowToast}
            setToastType={setToastType}
            setToastMessage={setToastMessage}
        />
    ) : null

    return (
        <>
            <PopoverButton>
                {({ onShowPopover, isOpen, anchorElement, onClosePopover }) => {
                    const dynamicOptions = OPTIONS.map((option) => {
                        if (option.value === 'DELETE') {
                            return {
                                ...option,
                                dataAnalytics: props.templateList?.isDefault
                                    ? 'MM-Template-Delete-defaultTemplate'
                                    : 'MM-Template-Delete'
                            }
                        }
                        return option
                    })
                    return (
                        <>
                            <AtlasTooltip
                                title={i18n.t('TEMPLATE_OPTION_MORE_LABEL') as string}
                                interactive
                                placement="bottom">
                                <span>
                                    <AtlasButton
                                        data-testid="MinutesManager_Overflow_Btn"
                                        data-analytics="MM-Template-Options"
                                        tabIndex={0}
                                        className="label-small-link"
                                        disableFocusRipple
                                        disableRipple
                                        onClick={onShowPopover}>
                                        <AtlasIcon name="more" size={24} />
                                    </AtlasButton>
                                </span>
                            </AtlasTooltip>
                            <Menu
                                open={isOpen}
                                anchorEl={anchorElement}
                                getContentAnchorEl={null}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                onClose={onClosePopover}>
                                {dynamicOptions.map((option) => {
                                    return (
                                        <MenuItem
                                            key={option.id}
                                            style={{ height: '55px' }}
                                            className={css`
                                                &:focus {
                                                    border: 2px solid ${ColorBaseSkyBlue};
                                                    border-radius: 2px;
                                                }
                                                &:hover {
                                                    background-color: #ebeef2;
                                                    border-radius: 2px;
                                                }
                                                ${option.value === 'DELETE'
                                                    ? 'border-top: 1px solid #ccc;'
                                                    : ''}
                                            `}
                                            data-analytics={option.dataAnalytics}
                                            data-testid={option.testid}
                                            onClick={() => {
                                                    handleCardOverflowClick(option.id, onClosePopover)
                                            }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginLeft: '10px',
                                                    marginRight: '20px',
                                                    flexDirection: 'row',
                                                    justifyContent: 'space-between'
                                                }}>
                                                <div style={{ paddingRight: '16px' }}>
                                                    {option.icon}
                                                </div>
                                                <div>{i18n.t(option.label)}</div>
                                            </div>
                                        </MenuItem>
                                    )
                                })}
                            </Menu>
                            {confirmationDialog}
                        </>
                    )
                }}
            </PopoverButton>
            {editDialog}
        </>
    )
}

export type OptionType = 'DEFAULT' | 'EDIT' | 'DOWNLOAD' | 'DELETE'

export default TemplatesCardOverflow
